import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useSidebarOpenState } from '@backstage/core-components';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import MemoryIcon from '@material-ui/icons/Memory';

// Fallback palette values — keep in sync with DfpSidebarNav
const FALLBACK_NAV_COLOR      = 'rgba(208, 208, 208, 0.82)';
const FALLBACK_SELECTED_COLOR = '#ffffff';
const FALLBACK_INDICATOR      = '#7df3e1';
const FALLBACK_HOVER_BG       = 'rgba(255, 255, 255, 0.05)';

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: string; // Material Symbol name
}

const NAV_ITEMS: NavItem[] = [
  { id: 'astra',  label: 'Astra',  path: '/ai-platform/astra',  icon: 'stars_2'        },
  { id: 'ai-ml',  label: 'AI/ML',  path: '/ai-platform/ai-ml',  icon: 'model_training' },
];

function isUnderAiPlatform(pathname: string): boolean {
  return pathname === '/ai-platform' || pathname.startsWith('/ai-platform/');
}

// ── Styles — identical tokens to DfpSidebarNav so both panels look consistent
const useStyles = makeStyles(theme => {
  const nav = theme.palette.navigation ?? {};
  const textColor      = nav.color                    ?? FALLBACK_NAV_COLOR;
  const selectedColor  = nav.selectedColor            ?? FALLBACK_SELECTED_COLOR;
  const indicatorColor = nav.indicator                ?? FALLBACK_INDICATOR;
  const hoverBg        = nav.navItem?.hoverBackground ?? FALLBACK_HOVER_BG;
  const isLightSidebar         = theme.palette.type === 'light';
  const activeBg               = isLightSidebar ? 'rgba(0, 0, 0, 0.20)' : 'rgba(255, 255, 255, 0.20)';
  const scrollThumb            = isLightSidebar ? 'rgba(0, 0, 0, 0.18)' : 'rgba(255, 255, 255, 0.12)';

  const sharedRow = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    borderRadius: 0,
    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    userSelect: 'none' as const,
  };

  const parentPad = { padding: '0 10px 0 23px' };

  const baseState = {
    color: textColor,
    backgroundColor: 'transparent',
    borderLeft: '3px solid transparent',
    '&:hover': { backgroundColor: hoverBg, color: selectedColor },
  };

  const activeState = {
    color: selectedColor,
    backgroundColor: activeBg,
    borderLeft: `3px solid ${indicatorColor}`,
    '&:hover': { backgroundColor: activeBg, color: selectedColor },
  };

  return {
    // ── Parent "AI Platform" row ──────────────────────────────────────────
    parentItem:       { ...sharedRow, ...parentPad, height: 48, ...baseState  },
    parentItemActive: { ...sharedRow, ...parentPad, height: 48, ...activeState },

    parentIcon: {
      minWidth: 'auto',
      marginRight: 12,
      color: 'inherit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': { fontSize: 20 },
    },
    parentText: {
      flex: '1 1 auto',
      minWidth: 0,
      margin: 0,
      '& .MuiListItemText-primary': {
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: '0.01em',
        color: 'inherit',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
      },
    },
    expandIcon: {
      fontSize: 24,
      color: 'inherit',
      opacity: 0.6,
      flexShrink: 0,
      marginLeft: 4,
      transition: 'opacity 0.15s ease',
    },
    expandIconOpen: { opacity: 0.85 },

    // ── Children panel ────────────────────────────────────────────────────
    childrenContainer: {
      width: '100%',
      display: 'block',
      overflowY: 'auto' as const,
      overflowX: 'hidden' as const,
      maxHeight: 160,
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': { background: scrollThumb, borderRadius: 2 },
    },

    // ── Child item ─────────────────────────────────────────────────────────
    // Two-layer pattern:
    //   childOuter — display:block, guarantees full sidebar width regardless
    //                of any flex context in the Collapse wrapper chain.
    //                Carries the background/border (via inline style) + hover.
    //   childInner — display:flex inside the outer block, handles icon+text.
    //                Padding is here (23px left = same as DFP nested items).
    childOuter: {
      display: 'block',
      width: '100%',
      height: 44,
      cursor: 'pointer',
      userSelect: 'none' as const,
      boxSizing: 'border-box' as const,
      transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      '&:hover': { backgroundColor: hoverBg, color: `${selectedColor} !important` },
    },
    childInner: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      // 23px left padding = same as DFP nested1 → sub-items align with DFP sub-items
      padding: '0 10px 0 23px',
      color: 'inherit',
      pointerEvents: 'none' as const,
    },
    childIcon: {
      fontFamily: 'Material Symbols Outlined',
      fontWeight: 'normal' as const,
      fontStyle: 'normal' as const,
      fontSize: 18,
      lineHeight: 1,
      flexShrink: 0,
      marginRight: 10,
      color: 'inherit',
      width: 18,
      textAlign: 'center' as const,
      fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20",
      transition: 'opacity 0.15s ease',
    },
    /** Hide ligature text until Material Symbols Outlined has loaded (avoids flashing "stars_2", etc.). */
    childIconFontPending: { opacity: 0 },
    childIconFontReady: { opacity: 1 },
    childLabel: {
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis' as const,
      whiteSpace: 'nowrap' as const,
      fontSize: 13,
      fontWeight: 500,
      letterSpacing: 0,
      lineHeight: 1.4,
      color: 'inherit',
    },

    // ── Collapsed (icon-only) state ───────────────────────────────────────
    closedItem:       { ...sharedRow, padding: 0, height: 48, justifyContent: 'center', ...baseState  },
    closedItemActive: { ...sharedRow, padding: 0, height: 48, justifyContent: 'center', ...activeState },
    closedItemIcon: {
      fontSize: 20,
      color: 'inherit',
    },

    // ── Nesting offset ─────────────────────────────────────────────────────
    nested1: { paddingLeft: 23 },  // aligns with parent icon column
  };
});

// ── AiPlatformSidebarNav ──────────────────────────────────────────────────

export const AiPlatformSidebarNav: React.FC = () => {
  const classes  = useStyles();
  const theme    = useTheme();
  const location = useLocation();
  const navigate  = useNavigate();
  const { isOpen } = useSidebarOpenState();
  const [expanded, setExpanded] = useState(false);
  const [materialSymbolsOutlinedReady, setMaterialSymbolsOutlinedReady] =
    useState(false);

  // Wait for Material Symbols font so ligatures render as icons, not literal names.
  useEffect(() => {
    let cancelled = false;
    const maxWaitMs = process.env.NODE_ENV === 'test' ? 0 : 3000;
    const timeout = new Promise<void>(resolve => {
      globalThis.setTimeout(resolve, maxWaitMs);
    });
    const run = async () => {
      try {
        await Promise.race([document.fonts.ready, timeout]);
      } catch {
        /* ignore */
      }
      if (!cancelled) {
        setMaterialSymbolsOutlinedReady(true);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  // Palette tokens used for child item inline styles
  const nav             = (theme.palette as any).navigation ?? {};
  const textColor       = nav.color                    ?? FALLBACK_NAV_COLOR;
  const selectedColor   = nav.selectedColor            ?? FALLBACK_SELECTED_COLOR;
  const indicatorColor  = nav.indicator                ?? FALLBACK_INDICATOR;
  const isLightSidebar  = theme.palette.type === 'light';
  const activeBg        = isLightSidebar ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)';

  // Auto-expand when navigating to any AI Platform route
  useEffect(() => {
    if (isUnderAiPlatform(location.pathname)) {
      setExpanded(true);
    }
  }, [location.pathname]);

  // Toggle expand/collapse only — do not navigate automatically.
  const handleParentClick = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const isActive = isUnderAiPlatform(location.pathname);

  // Collapsed sidebar — show only the icon
  if (!isOpen) {
    return (
      <ListItem
        button
        className={isActive ? classes.closedItemActive : classes.closedItem}
        onClick={() => navigate('/ai-platform/astra')}
        disableGutters
      >
        <MemoryIcon className={classes.closedItemIcon} />
      </ListItem>
    );
  }

  return (
    <>
      {/* Parent row */}
      <ListItem
        className={isActive ? classes.parentItemActive : classes.parentItem}
        onClick={handleParentClick}
        disableGutters
      >
        <ListItemIcon className={classes.parentIcon}>
          <MemoryIcon />
        </ListItemIcon>
        <ListItemText primary="AI Platform" className={classes.parentText} />
        {expanded ? (
          <ArrowDropDownIcon className={`${classes.expandIcon} ${classes.expandIconOpen}`} />
        ) : (
          <ArrowRightIcon className={classes.expandIcon} />
        )}
      </ListItem>

      {/* Expandable children — uses two-div pattern (childOuter + childInner)
          for guaranteed full-width backgrounds + working Material Symbols icons. */}
      <Collapse in={expanded} timeout={200} unmountOnExit style={{ width: '100%' }}>
        <div className={classes.childrenContainer} style={{ width: '100%' }}>
          {NAV_ITEMS.map(item => {
            const itemActive =
              location.pathname === item.path ||
              location.pathname.startsWith(`${item.path}/`);

            return (
              <button
                key={item.id}
                type="button"
                className={classes.childOuter}
                style={{
                  width: '100%',
                  color:           itemActive ? selectedColor : textColor,
                  backgroundColor: itemActive ? activeBg      : 'transparent',
                  borderLeft:      itemActive
                    ? `3px solid ${indicatorColor}`
                    : '3px solid transparent',
                  border: 'none',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  fontWeight:      itemActive ? 700 : 500,
                  padding: 0,
                }}
                onClick={() => navigate(item.path)}
              >
                <div className={classes.childInner}>
                  <span
                    aria-hidden
                    className={`${classes.childIcon} ${
                      materialSymbolsOutlinedReady
                        ? classes.childIconFontReady
                        : classes.childIconFontPending
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span className={classes.childLabel}>
                    {item.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </Collapse>
    </>
  );
};
